/* eslint-disable */
import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  X,
  Camera,
  Sparkles,
  DollarSign,
  MessageSquarePlus,
  ArrowRight,
  ArrowLeft,
  Package,
  PackagePlus,
  Search,
  Check,
  ImagePlus,
  Wand2,
  Type,
  Minus,
  ExternalLink,
  PartyPopper,
} from "lucide-react";
import {
  useAIGeneration,
  PIPELINE_STEPS,
  resolveFreeRemaining,
} from "../../../context/AIProductGenerationContext";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { updateAccount } from "../../../redux/account/account";

const LANDING_COST = 40;
const MIN_TOKENS_AFTER = -50;

const STEPS_NEW = ["source", "image", "name", "price", "instructions"];
const STEPS_EXISTING = ["source", "selectProduct", "instructions"];

function extractPrice(product) {
  const isPositive = (v) =>
    v !== undefined && v !== null && v !== "" && Number(v) > 0;
  if (isPositive(product?.price)) return product.price;
  if (Array.isArray(product?.offers)) {
    const offer = product.offers.find((o) => o && isPositive(o.price));
    if (offer) return offer.price;
  }
  if (Array.isArray(product?.options)) {
    const option = product.options.find((o) => o && isPositive(o.price));
    if (option) return option.price;
  }
  return null;
}

function extractImage(product) {
  return (
    product?.image ||
    product?.newImage ||
    (Array.isArray(product?.gallery) && product.gallery[0]) ||
    (Array.isArray(product?.newGallery) && product.newGallery[0]) ||
    null
  );
}

function normalizeProduct(raw) {
  return {
    id: raw?.id,
    name: raw?.name || "",
    price: extractPrice(raw),
    currency: raw?.currency || "DZD",
    image: extractImage(raw),
    raw,
  };
}

const initialFormState = {
  productSource: null,
  selectedProduct: null,
  imageFile: null,
  imagePreview: "",
  productName: "",
  price: "",
  currency: "DZD",
  instructions: "",
};

export default function AILandingPageModal({
  show,
  setShow,
  existingProducts = [],
  onGenerate,
  isGenerating: isGeneratingProp = false,
}) {
  const account = useSelector((state) => state.AccountReducer);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const aiGen = useAIGeneration();
  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState(initialFormState);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const fileInputRef = useRef(null);

  const [freeRemaining, setFreeRemaining] = useState(null);
  const subscription = account?.subscription || {};
  const tokens = Number(subscription.tokens || 0);

  const effectiveFree = resolveFreeRemaining(subscription, freeRemaining);
  const willUseFree = effectiveFree > 0;

  useEffect(() => {
    if (!show) return;
    setFreeRemaining(resolveFreeRemaining(account?.subscription || {}));
  }, [
    show,
    account?.subscription?.aiProductPageFreeRemaining,
    account?.subscription?.aiProductPageFreeMonth,
  ]);

  function isUsableProductImage(url) {
    if (!url || typeof url !== "string") return false;
    const u = url.trim().toLowerCase();
    if (!u.startsWith("http://") && !u.startsWith("https://")) return false;

    if (u.includes("example.com")) return false;
    if (u.includes("dummy")) return false;
    if (u.includes("placeholder")) return false;
    if (u.endsWith("/null") || u.endsWith("/undefined")) return false;

    return true;
  }

  const AI_CREATED_VIA = new Set([
    "FoorAiLandingPageGenerator",
    "FoorAiProductPageGenerator",
  ]);

  const normalizedProducts = useMemo(
    () =>
      (existingProducts || []).map(normalizeProduct).filter((p) => {
        const raw = p.raw;
        if (!isUsableProductImage(p.image)) return false;
        if (raw?.created_via && AI_CREATED_VIA.has(raw.created_via)) {
          return false;
        }
        return true;
      }),
    [existingProducts],
  );

  const steps = form.productSource === "existing" ? STEPS_EXISTING : STEPS_NEW;
  const stepKey = steps[stepIndex];
  const totalSteps = steps.length;
  const isSourceStep = stepKey === "source";
  const isInstructionsStep = stepKey === "instructions";
  const phase = aiGen.phase;
  const isBusy = phase === "generating" || isGeneratingProp;

  const resetAll = useCallback(() => {
    setStepIndex(0);
    setForm(initialFormState);
    setShowCloseConfirm(false);
    setGenerationError("");
    aiGen.reset();
  }, [aiGen]);

  const close = useCallback(() => {
    setShow(false);
    setTimeout(resetAll, 220);
  }, [setShow, resetAll]);

  const requestClose = useCallback(() => {
    if (aiGen.phase === "generating") {
      setShowCloseConfirm(true);
      return;
    }
    close();
  }, [aiGen.phase, close]);

  const buildPayload = useCallback(() => {
    const base = {
      user: account?.user || "",
      generation_prompt: form.instructions.trim(),
      generation_language: "ar-DZ",
    };
    if (form.productSource === "existing") {
      const selected = form.selectedProduct;
      const currency = selected?.currency || "DZD";
      return {
        ...base,
        product_id: selected?.id ?? null,
        product_name: selected?.name || "",
        product_price:
          selected?.price != null ? `${selected.price} ${currency}` : null,
        product_image: selected?.image ?? null,
      };
    }
    return {
      ...base,
      product_image: form.imageFile,
      product_name: form.productName.trim(),
      product_price: `${form.price.trim()} ${form.currency}`,
    };
  }, [form, account?.user]);

  const startGeneration = useCallback(() => {
    setGenerationError("");

    const localFree = resolveFreeRemaining(subscription, freeRemaining);
    const hasFree = localFree > 0;

    if (!hasFree && tokens - LANDING_COST < MIN_TOKENS_AFTER) {
      setGenerationError("INSUFFICIENT_TOKENS");
      aiGen.setPhase?.("done");
      return;
    }

    const payload = buildPayload();

    aiGen.startGeneration({
      feature: "landing_page",
      onFinish: async () => {},
    });

    (async () => {
      try {
        const data = await onGenerate(payload);
        if (!data?.ok || !data?.product) {
          throw new Error(data?.error);
        }

        if (data.billing) {
          const monthKey = data.billing.monthKey || undefined;
          dispatch(
            updateAccount({
              subscription: {
                ...(account.subscription || {}),
                ...(typeof data.billing.tokens === "number"
                  ? { tokens: data.billing.tokens }
                  : {}),
                ...(typeof data.billing.freeRemaining === "number"
                  ? { aiProductPageFreeRemaining: data.billing.freeRemaining }
                  : {}),
                ...(monthKey ? { aiProductPageFreeMonth: monthKey } : {}),
              },
            }),
          );
          if (typeof data.billing.freeRemaining === "number") {
            setFreeRemaining(data.billing.freeRemaining);
          }
        }

        if (typeof aiGen.completeBuildStep === "function") {
          aiGen.completeBuildStep();
        } else {
          aiGen.setPipelineDone?.(PIPELINE_STEPS.map((s) => s.id));
          aiGen.setFillProgress?.(1);
          aiGen.setPipelineIndex?.(PIPELINE_STEPS.length);
        }

        aiGen.setPhase?.("done");
      } catch (err) {
        const isInsufficient =
          err?.code === "INSUFFICIENT_TOKENS" ||
          err?.response?.status === 402 ||
          String(err?.message || "").includes("INSUFFICIENT");

        const isNetwork =
          err?.code === "NETWORK" ||
          err?.code === "ERR_NETWORK" ||
          err?.message === "Network Error";

        const isTimeout =
          err?.code === "TIMEOUT" ||
          err?.code === "ECONNABORTED" ||
          String(err?.message || "")
            .toLowerCase()
            .includes("timeout");

        const isServer =
          err?.code === "SERVER" ||
          (typeof err?.response?.status === "number" &&
            err.response.status >= 500);

        let errorCode = "GENERIC";
        if (isInsufficient) errorCode = "INSUFFICIENT_TOKENS";
        else if (isNetwork) errorCode = "NETWORK";
        else if (isTimeout) errorCode = "TIMEOUT";
        else if (isServer) errorCode = "SERVER";

        setGenerationError(errorCode);
        if (typeof aiGen.completeAllStepsNow === "function") {
          aiGen.completeAllStepsNow();
        }
        aiGen.setPhase?.("done");
      }
    })();
  }, [
    tokens,
    buildPayload,
    onGenerate,
    aiGen,
    account,
    dispatch,
    freeRemaining,
    subscription,
  ]);

  const handleRetryFromInstructions = useCallback(() => {
    setGenerationError("");
    aiGen.setPhase?.("idle");
    aiGen.setPipelineDone?.([]);
    aiGen.setFillProgress?.(0);
    aiGen.setPipelineIndex?.(-1);
    const idx = steps.indexOf("instructions");
    setStepIndex(idx >= 0 ? idx : steps.length - 1);
  }, [aiGen, steps]);

  const canContinue = () => {
    if (stepKey === "source") return !!form.productSource;
    if (stepKey === "selectProduct") return !!form.selectedProduct;
    if (stepKey === "image") return !!form.imageFile;
    if (stepKey === "name") return form.productName.trim().length > 0;
    if (stepKey === "price") return form.price.trim().length > 0;
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({
      ...prev,
      imageFile: file,
      imagePreview: URL.createObjectURL(file),
    }));
  };

  const chooseSourceAndAdvance = (source) => {
    setForm({ ...initialFormState, productSource: source });
    setStepIndex(1);
  };

  const handleNext = () => {
    if (!canContinue() || isBusy) return;
    if (isInstructionsStep) {
      startGeneration();
      return;
    }
    if (stepIndex < totalSteps - 1) setStepIndex((s) => s + 1);
  };

  const handleBack = () => {
    if (isBusy || phase === "done") return;
    if (stepIndex > 0) setStepIndex((s) => s - 1);
  };

  const handleMinimize = () => {
    if (phase === "generating" || phase === "done") {
      aiGen.minimize();
    }
  };

  const canGoNext = canContinue() && !isBusy && phase === "idle";

  const closeConfirm = showCloseConfirm
    ? createPortal(
        <CloseConfirmDialog
          t={t}
          isRTL={isRTL}
          onStay={() => setShowCloseConfirm(false)}
          onLeave={() => {
            setShowCloseConfirm(false);
            close();
          }}
        />,
        document.body,
      )
    : null;

  // Same as product: when minimized, orb is rendered by AIProductGenerationContext / layout
  if (aiGen.isMinimized) return null;
  if (!show && phase === "idle") return null;

  if (phase === "generating") {
    return (
      <>
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#1a1025]/75 p-0 sm:p-6"
          onClick={handleMinimize}
        >
          <div
            dir={isRTL ? "rtl" : "ltr"}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md bg-white sm:rounded-[28px] rounded-t-[28px] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]"
          >
            <GeneratingView
              t={t}
              pipelineIndex={aiGen.pipelineIndex}
              pipelineDone={aiGen.pipelineDone}
              fillProgress={aiGen.fillProgress}
              onMinimize={handleMinimize}
            />
          </div>
        </div>
        {closeConfirm}
      </>
    );
  }

  if (phase === "done") {
    return (
      <>
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#1a1025]/75 p-0 sm:p-6">
          <div
            dir={isRTL ? "rtl" : "ltr"}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md bg-white sm:rounded-[28px] rounded-t-[28px] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]"
          >
            <DoneView
              t={t}
              error={generationError}
              onClose={close}
              onMinimize={handleMinimize}
              onRetry={handleRetryFromInstructions}
              onSeeProduct={() => {
                close();
                navigate("/landing-page");
              }}
              onGoToPlan={() => {
                close();
                const digital =
                  (account.productMode ?? "classic") === "digital";
                navigate(digital ? "/digital/plan" : "/plan");
              }}
            />
          </div>
        </div>
        {closeConfirm}
      </>
    );
  }

  if (!show) return null;

  if (isSourceStep) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#1a1025]/70 p-0 sm:p-6"
        onClick={requestClose}
      >
        <div
          dir={isRTL ? "rtl" : "ltr"}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-lg bg-white sm:rounded-[28px] rounded-t-[28px] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]"
        >
          <div className="relative h-44 sm:h-52 overflow-hidden bg-gradient-to-br from-[#9b59bf] to-[#be97d4]">
            <img
              src="/landingpageAi.png"
              alt=""
              className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-40"
              draggable={false}
            />
            <button
              type="button"
              onClick={requestClose}
              className="absolute top-4 end-4 z-10 w-9 h-9 rounded-full bg-black/30 text-white flex items-center justify-center"
              aria-label={t("video.close")}
            >
              <X size={18} />
            </button>
            {!willUseFree && (
              <span className="text-[11px] font-bold text-[#9b59bf] bg-purple-50 border border-purple-100 rounded-full px-2.5 py-1">
                {t("landing.token_cost", { cost: LANDING_COST })}
              </span>
            )}
            <div className="absolute bottom-5 start-5 end-5">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[11px] font-medium text-white mb-2 border border-white/20">
                <Wand2 size={12} />
                {t("landing.ai_powered")}
              </div>
              <h2 className="text-white text-xl sm:text-2xl font-bold">
                {t("landing.source_hero_title")}
              </h2>
            </div>
          </div>

          <div className="p-5 sm:p-6 overflow-y-auto">
            <p className="text-sm text-gray-500 mb-5">
              {t("landing.step_source_subtitle")}
            </p>

            <button
              type="button"
              onClick={() => chooseSourceAndAdvance("existing")}
              className="w-full text-start rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white p-4 mb-3 hover:border-[#9b59bf] transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#9b59bf] text-white flex items-center justify-center">
                  <Package size={22} />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-gray-900 text-[15px] mb-1">
                    {t("landing.source_existing_title")}
                  </div>
                  <p className="text-xs text-gray-500">
                    {t("landing.source_existing_desc")}
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => chooseSourceAndAdvance("new")}
              className="w-full text-start rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white p-4 hover:border-[#9b59bf] transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#9b59bf] text-white flex items-center justify-center">
                  <PackagePlus size={22} />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-gray-900 text-[15px] mb-1">
                    {t("landing.source_new_title")}
                  </div>
                  <p className="text-xs text-gray-500">
                    {t("landing.source_new_desc")}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
        {closeConfirm}
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#1a1025]/70 p-0 sm:p-6"
        onClick={requestClose}
      >
        <div
          dir={isRTL ? "rtl" : "ltr"}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-xl bg-white sm:rounded-[28px] rounded-t-[28px] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]"
        >
          <div className="shrink-0 px-5 pt-4 pb-3 flex items-center justify-between gap-3 border-b border-gray-100">
            <button
              type="button"
              onClick={requestClose}
              className="w-9 h-9 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-1.5 flex-1 justify-center max-w-[200px]">
              {steps.slice(1).map((s, i) => {
                const realIndex = i + 1;
                const done = realIndex < stepIndex;
                const current = realIndex === stepIndex;
                return (
                  <div
                    key={s}
                    className={`h-1.5 flex-1 rounded-full ${
                      done || current ? "bg-[#9b59bf]" : "bg-gray-200"
                    } ${current ? "opacity-70" : ""}`}
                  />
                );
              })}
            </div>
            {!willUseFree && (
              <span className="text-[11px] font-bold text-[#9b59bf] bg-purple-50 border border-purple-100 rounded-full px-2.5 py-1">
                {t("landing.token_cost", { cost: LANDING_COST })}
              </span>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-6">
            {stepKey === "selectProduct" && (
              <SelectProductStep
                form={form}
                setForm={setForm}
                existingProducts={normalizedProducts}
                t={t}
              />
            )}
            {stepKey === "image" && (
              <ImageStep
                form={form}
                fileInputRef={fileInputRef}
                onFileChange={handleFileChange}
                t={t}
              />
            )}
            {stepKey === "name" && (
              <NameStep form={form} setForm={setForm} t={t} />
            )}
            {stepKey === "price" && (
              <PriceStep form={form} setForm={setForm} t={t} />
            )}
            {stepKey === "instructions" && (
              <InstructionsStep form={form} setForm={setForm} t={t} />
            )}
          </div>

          <div className="shrink-0 px-5 sm:px-6 py-4 border-t border-gray-100 flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleBack}
                className="h-12 px-4 rounded-2xl border border-gray-200 text-gray-600 text-sm font-medium"
              >
                {isRTL ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={!canGoNext}
                className="flex-1 h-12 rounded-2xl bg-[#9b59bf] hover:bg-[#814a9f] disabled:opacity-45 text-white text-sm font-semibold flex items-center justify-center gap-2"
              >
                {isInstructionsStep ? (
                  <>
                    <Sparkles size={16} />
                    {t("landing.create_landing")}
                    {!willUseFree && (
                      <span className="font-bold">−{LANDING_COST}</span>
                    )}
                  </>
                ) : (
                  <>
                    {t("landing.continue")}
                    {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                  </>
                )}
              </button>
            </div>
            {isInstructionsStep && !willUseFree && (
              <p className="text-[11px] text-center text-red-600">
                {t("landing.cost_note", { cost: LANDING_COST })}
              </p>
            )}
          </div>
        </div>
      </div>
      {closeConfirm}
    </>
  );
}

/* —— shared subviews (same pattern as product) —— */

function GeneratingView({
  t,
  pipelineIndex,
  pipelineDone,
  fillProgress,
  onMinimize,
}) {
  return (
    <div className="relative flex flex-col min-h-[420px] sm:min-h-[480px]">
      <AmbientShapes progress={fillProgress} />
      <div className="relative z-10 flex items-center justify-between px-4 pt-4">
        <button
          type="button"
          onClick={onMinimize}
          className="w-9 h-9 rounded-xl bg-white/80 border border-gray-200 text-gray-500 flex items-center justify-center"
        >
          <Minus size={18} />
        </button>
      </div>
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-purple-50 border border-purple-100 px-8 py-2">
          <Sparkles size={14} className="text-[#9b59bf] animate-pulse" />
          <span className="text-xs font-semibold text-[#9b59bf]">
            {t("landing.generating_title")}
          </span>
        </div>
        <ul className="w-full max-w-xs space-y-4">
          {PIPELINE_STEPS.map((step, i) => {
            const isDone = pipelineDone.includes(step.id);
            const isActive = pipelineIndex === i && !isDone;
            const isPending = pipelineIndex < i;
            return (
              <li
                key={step.id}
                className={`flex items-center gap-3 ${
                  isPending ? "opacity-40" : "opacity-100"
                }`}
              >
                <PipelineCircle done={isDone} active={isActive} />
                <span
                  className={`text-sm font-medium ${
                    isDone
                      ? "text-emerald-700"
                      : isActive
                        ? "text-gray-900"
                        : "text-gray-400"
                  }`}
                >
                  {t(step.labelKey, { defaultValue: step.defaultLabel })}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function PipelineCircle({ done, active }) {
  return (
    <div
      className={`relative w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
        done
          ? "bg-emerald-500"
          : active
            ? "bg-white border-2 border-dashed border-[#9b59bf] animate-[spin_5s_linear_infinite]"
            : "border-2 border-dashed border-gray-300"
      }`}
    >
      {done && <Check size={14} className="text-white" strokeWidth={3} />}
    </div>
  );
}

function AmbientShapes({ progress }) {
  const shapes = [
    {
      size: 56,
      top: "9%",
      left: "25%",
      delay: "0s",
      rotate: 12,
      from: "#9b59bf",
      to: "#c4b5fd",
    },
    {
      size: 40,
      top: "18%",
      right: "14%",
      delay: "0.4s",
      rotate: -8,
      from: "#7c3aed",
      to: "#a78bfa",
    },
    {
      size: 72,
      bottom: "22%",
      left: "8%",
      delay: "0.8s",
      rotate: 20,
      from: "#9b59bf",
      to: "#ddd6fe",
    },
    {
      size: 48,
      bottom: "12%",
      right: "10%",
      delay: "1.2s",
      rotate: -15,
      from: "#7c3aed",
      to: "#c4b5fd",
    },
  ];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {shapes.map((s, i) => (
        <div
          key={i}
          className="absolute rounded-2xl"
          style={{
            width: s.size,
            height: s.size,
            top: s.top,
            left: s.left,
            right: s.right,
            bottom: s.bottom,
          }}
        >
          <div
            className="absolute inset-0 rounded-2xl border-2 border-[#9b59bf]/40"
            style={{ transform: `rotate(${s.rotate}deg)` }}
          />
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              transform: `rotate(${s.rotate}deg)`,
              clipPath: `inset(${Math.max(0, 100 - progress * 100)}% 0 0 0)`,
              background: `linear-gradient(to top, ${s.from}99, ${s.to}66)`,
            }}
          />
        </div>
      ))}
    </div>
  );
}

function DoneView({
  t,
  error,
  onClose,
  onMinimize,
  onRetry,
  onSeeProduct,
  onGoToPlan,
}) {
  const isInsufficient = error === "INSUFFICIENT_TOKENS";

  const errorMessage = (() => {
    switch (error) {
      case "INSUFFICIENT_TOKENS":
        return t("foorAi.errors.insufficient_text", { cost: 40 });
      case "NETWORK":
        return t("foorAi.errors.error_network");
      case "TIMEOUT":
        return t("foorAi.errors.error_timeout");
      case "SERVER":
        return t("foorAi.errors.error_server");
      case "GENERIC":
      default:
        return t("foorAi.errors.generic");
    }
  })();

  return (
    <div className="relative flex flex-col min-h-[380px]">
      <div className="flex items-center justify-between px-4 pt-4">
        <button
          type="button"
          onClick={onMinimize}
          className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center"
        >
          <Minus size={18} />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center"
        >
          <X size={18} />
        </button>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 text-center">
        {error ? (
          <>
            <p className="text-sm text-red-600 mb-6">{errorMessage}</p>
            {isInsufficient ? (
              <button
                type="button"
                onClick={onGoToPlan}
                className="h-12 px-6 rounded-2xl bg-[#9b59bf] text-white text-sm font-semibold"
              >
                {t("landing.recharge")}
              </button>
            ) : (
              <button
                type="button"
                onClick={onRetry}
                className="h-12 px-6 rounded-2xl bg-[#9b59bf] text-white text-sm font-semibold"
              >
                {t("landing.start_again")}
              </button>
            )}
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-5">
              <Check size={36} className="text-white" strokeWidth={2.5} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t("landing.done_title")}
            </h3>
            <p className="text-sm text-gray-500 mb-8">
              {t("landing.done_subtitle")}
            </p>
            <button
              type="button"
              onClick={onSeeProduct}
              className="h-12 px-6 rounded-2xl bg-[#9b59bf] text-white text-sm font-semibold inline-flex items-center gap-2"
            >
              <ExternalLink size={16} />
              {t("landing.see_product")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function CloseConfirmDialog({ t, isRTL, onStay, onLeave }) {
  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4">
      <div
        dir={isRTL ? "rtl" : "ltr"}
        className="w-full max-w-sm bg-white rounded-2xl p-5"
      >
        <h3 className="text-base font-bold mb-2">
          {t("landing.close_confirm_title")}
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          {t("landing.close_confirm_body")}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onStay}
            className="flex-1 h-11 rounded-xl bg-[#9b59bf] text-white text-sm font-semibold"
          >
            {t("landing.stay")}
          </button>
          <button
            type="button"
            onClick={onLeave}
            className="flex-1 h-11 rounded-xl border border-red-200 text-red-600 text-sm font-semibold"
          >
            {t("landing.leave")}
          </button>
        </div>
      </div>
    </div>
  );
}

function StepHeading({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-2xl bg-purple-100 text-[#9b59bf] flex items-center justify-center">
          <Icon size={18} />
        </div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      </div>
      {subtitle && (
        <p className="text-sm text-gray-500 ps-[52px]">{subtitle}</p>
      )}
    </div>
  );
}

function SelectProductStep({ form, setForm, existingProducts, t }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!query.trim()) return existingProducts;
    const q = query.trim().toLowerCase();
    return existingProducts.filter((p) => p.name?.toLowerCase().includes(q));
  }, [existingProducts, query]);

  return (
    <div>
      <StepHeading
        icon={Search}
        title={t("landing.step_select_product_title")}
        subtitle={t("landing.step_select_product_subtitle")}
      />
      <div className="relative mb-3">
        <Search
          size={16}
          className="absolute top-1/2 -translate-y-1/2 start-3.5 text-gray-400"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("landing.search_product_placeholder")}
          className="w-full h-11 rounded-xl border border-gray-200 bg-gray-50 ps-10 pe-3 text-sm"
        />
      </div>
      <div className="max-h-60 overflow-y-auto space-y-2">
        {filtered.map((product) => {
          const selected = form.selectedProduct?.id === product.id;
          return (
            <button
              key={product.id}
              type="button"
              onClick={() =>
                setForm((p) => ({ ...p, selectedProduct: product }))
              }
              className={`w-full flex items-center gap-3 rounded-xl border p-2.5 text-start ${
                selected ? "border-[#9b59bf] bg-purple-50" : "border-gray-200"
              }`}
            >
              <div className="w-11 h-11 rounded-lg bg-gray-100 overflow-hidden">
                {product.image ? (
                  <img
                    src={product.image}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package size={16} className="m-auto text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">
                  {product.name}
                </div>
                {product.price != null && (
                  <div className="text-xs text-gray-500">
                    {product.price} {product.currency || ""}
                  </div>
                )}
              </div>
              {selected && (
                <div className="w-5 h-5 rounded-full bg-[#9b59bf] flex items-center justify-center">
                  <Check size={11} className="text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ImageStep({ form, fileInputRef, onFileChange, t }) {
  return (
    <div>
      <StepHeading
        icon={ImagePlus}
        title={t("landing.step_image_title")}
        subtitle={t("landing.step_image_subtitle")}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFileChange}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="w-full rounded-2xl border-2 border-dashed border-purple-200 overflow-hidden"
      >
        {form.imagePreview ? (
          <div className="aspect-[4/3] bg-gray-50">
            <img
              src={form.imagePreview}
              alt=""
              className="w-full h-full object-contain"
            />
          </div>
        ) : (
          <div className="aspect-[4/3] flex flex-col items-center justify-center gap-3">
            <Camera size={26} className="text-[#9b59bf]" />
            <span className="text-sm font-semibold">
              {t("landing.take_photo")}
            </span>
          </div>
        )}
      </button>
    </div>
  );
}

function NameStep({ form, setForm, t }) {
  return (
    <div>
      <StepHeading icon={Type} title={t("landing.step_name_title")} />
      <input
        type="text"
        autoFocus
        value={form.productName}
        onChange={(e) =>
          setForm((p) => ({ ...p, productName: e.target.value }))
        }
        className="w-full h-14 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-base font-medium"
        placeholder={t("landing.name_placeholder")}
      />
    </div>
  );
}

function PriceStep({ form, setForm, t }) {
  return (
    <div>
      <StepHeading icon={DollarSign} title={t("landing.step_price_title")} />
      <div className="flex gap-2">
        <input
          type="number"
          inputMode="decimal"
          autoFocus
          value={form.price}
          onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
          className="flex-1 h-14 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-2xl font-bold"
          placeholder="0"
        />
        <div className="h-14 min-w-[72px] rounded-2xl bg-purple-50 border border-purple-100 flex items-center justify-center text-sm font-bold text-[#9b59bf]">
          {form.currency || "DZD"}
        </div>
      </div>
    </div>
  );
}

function InstructionsStep({ form, setForm, t }) {
  return (
    <div>
      <StepHeading
        icon={MessageSquarePlus}
        title={t("landing.step_instructions_title")}
      />
      <textarea
        rows={4}
        value={form.instructions}
        onChange={(e) =>
          setForm((p) => ({ ...p, instructions: e.target.value }))
        }
        className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm"
        placeholder={t("landing.instructions_placeholder")}
      />
    </div>
  );
}
